<script setup lang="ts">
import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import uuidv7 from '@/helpers/uuidv7'
import { useDatabaseStore } from '@/stores/databaseStore'
import { computed, ref } from 'vue'
import BaseButton from './BaseButton.vue'
import type { DataTableColumn } from './BaseDataTable.vue'
import BaseDataTable from './BaseDataTable.vue'

const databaseStore = useDatabaseStore()
const selectedCostSource = ref(databaseStore.costSources?.find((c) => c))
const showNewInput = ref(false)
const newLaborTypeName = ref<string>()

const rows = computed(() => {
  return (
    databaseStore.laborTypes?.map((laborType) => {
      const laborRate = databaseStore.laborRates?.find(
        (laborRate) =>
          laborRate.labor_type_id == laborType.id &&
          laborRate.cost_source_id == selectedCostSource.value?.id,
      )
      return {
        laborTypeId: laborType.id,
        laborTypeName: laborType.name,
        laborRateRate: laborRate?.rate,
        laborRateId: laborRate?.id,
      }
    }) ?? []
  )
})
type RowType = (typeof rows.value)[number]
const columns: DataTableColumn<RowType>[] = [
  {
    columnDataKey: 'laborTypeName',
    name: 'Name',
  },
  {
    columnDataKey: 'laborRateRate',
    name: 'Rate',
    columnType: 'number',
  },
]

async function addCostSource() {
  if (!newLaborTypeName.value) throw toastThrowError('New labor type must have a name')
  const { data, error } = await supabase
    .from('labor_type')
    .insert({ id: uuidv7(), name: newLaborTypeName.value })
    .select()
    .single()
  if (error) throw toastThrowError('Error saving new labor type', error)
  databaseStore.laborTypes?.push(data)
  newLaborTypeName.value = undefined
}

async function updateLaborType(row: RowType) {
  const { data, error } = await supabase
    .from('labor_type')
    .update({ name: row.laborTypeName })
    .eq('id', row.laborTypeId)
    .select()
    .single()
  if (error) throw toastThrowError('Error updating new labor type', error)
  const index = databaseStore.laborTypes?.findIndex((t) => t.id == row.laborTypeId)
  if (index == undefined) throw toastThrowError('Error updating new labor type. Index not found')
  if (!selectedCostSource.value) throw toastThrowError('Cost source must be selected')
  databaseStore.laborTypes?.splice(index, 1, data)
  if (row.laborRateId) {
    // Update
    if (!row.laborRateRate) {
      // Deleted labor rate
      const rateResult = await supabase.from('labor_rate').delete().eq('id', row.laborRateId)
      if (rateResult.error) throw toastThrowError('Error removing labor rate', rateResult.error)
      const laborRateIndex = databaseStore.laborRates?.findIndex((lr) => lr.id == row.laborRateId)
      if (laborRateIndex == undefined)
        throw toastThrowError('Error: Unable to get labor rate index')
      databaseStore.laborRates?.splice(laborRateIndex, 1)
      toast('Removed labor type')
    } else {
      // Regular Update
      const { data: rateData, error: rateError } = await supabase
        .from('labor_rate')
        .update({
          rate: row.laborRateRate,
        })
        .eq('id', row.laborRateId)
        .select()
        .single()
      if (rateError) throw toastThrowError('Error updating labor rate', rateError)
      const laborRateIndex = databaseStore.laborRates?.findIndex((lr) => lr.id == row.laborRateId)
      if (laborRateIndex == undefined)
        throw toastThrowError('Error: Unable to get labor rate index')
      databaseStore.laborRates?.splice(laborRateIndex, 1, rateData)
      toast('Updated labor type')
    }
  } else {
    // Add new
    const { data: rateData, error: rateError } = await supabase
      .from('labor_rate')
      .insert({
        id: uuidv7(),
        cost_source_id: selectedCostSource.value.id,
        labor_type_id: row.laborTypeId,
        rate: row.laborRateRate,
      })
      .select()
      .single()
    if (rateError) throw toastThrowError('Error adding labor rate', rateError)
    databaseStore.laborRates?.push(rateData)
    toast('Added labor rate')
  }
}
</script>

<template>
  <div class="flex h-full w-full flex-col gap-3 p-3">
    <div class="flex">
      <label for="costSource">Cost Source: </label>
      <select v-model="selectedCostSource" class="bg-white focus:border">
        <option
          v-for="costSource in databaseStore.costSources"
          :key="costSource.id"
          :value="costSource"
        >
          {{ costSource.name }}
        </option>
      </select>
    </div>
    <div>
      <BaseDataTable
        table-name="DBLabor"
        :columns="columns"
        :rows="rows"
        :row-key="'laborTypeId'"
        @row-update="updateLaborType"
      />
    </div>
    <div v-if="showNewInput" class="flex w-full flex-nowrap">
      <!-- New source input -->
      <input
        id="name"
        v-model="newLaborTypeName"
        class="w-full border p-1"
        placeholder="Cost Source Name"
      />
      <button @click="addCostSource()">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          class="size-6"
        >
          <path
            fill-rule="evenodd"
            d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z"
            clip-rule="evenodd"
          />
        </svg>
      </button>
    </div>
    <BaseButton
      class="w-full cursor-pointer p-1 font-bold hover:bg-blue-200"
      @click="showNewInput = true"
    >
      New Labor Type
    </BaseButton>
  </div>
</template>
